import React, { ReactNode, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ContentContainer,
  TextContainer,
  MainTitle,
  SubTitle,
  MediaContainer,
  Image,
  Video,
  ButtonContainer,
  KioskButton,
  BannerGrid,
  BannerImage,
} from "./styles";
import {
  KIOSK_VIDEO_LIVE_USE,
  KIOSK_VIDEO_MEET_TEACHER,
  KIOSK_VIDEO_SELF_TREATMENT,
  BANNER_IMAGE_BASE_URL,
} from "../../../constants/urls";

interface MediaItem {
  type: "image" | "video";
  src: string;
  alt?: string;
  maxWidth?: number;
}

interface BannerItem {
  id: number;
  fileName: string;
}

interface ContentSectionProps {
  mainTitle: string;
  subTitle?: string;
  media?: MediaItem[];
  banners?: BannerItem[];
  direction?: "column" | "row" | "row-reverse";
  textAlign?: "left" | "center" | "right";
  children?: ReactNode;
}

const fadeInUp = {
  hidden: { opacity: 0, y: -30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: 0.2,
    },
  },
};

const ContentSection: React.FC<ContentSectionProps> = ({
  mainTitle,
  subTitle,
  media,
  banners,
  direction = "column",
  textAlign = "left",
  children,
}) => {
  const [currentVideo, setCurrentVideo] = useState<string | null>(
    KIOSK_VIDEO_LIVE_USE
  );
  const [currentMaxWidth, setCurrentMaxWidth] = useState<number | undefined>(
    800
  );
  const [randomBanners, setRandomBanners] = useState<BannerItem[]>([]);

  const handleButtonClick = (videoSrc: string, maxWidth?: number) => {
    setCurrentVideo(videoSrc);
    setCurrentMaxWidth(maxWidth);
  };

  useEffect(() => {
    if (banners && banners.length > 0) {
      // 16개 랜덤 선택
      const shuffled = [...banners].sort(() => 0.5 - Math.random());
      setRandomBanners(shuffled.slice(0, 16));
    }
  }, [banners]);

  const handleImageError = (id: number) => {
    // 이미지 로드 실패 시 다른 배너로 대체
    if (banners && banners.length > 0) {
      const newBanner = banners[Math.floor(Math.random() * banners.length)];
      setRandomBanners((prev) =>
        prev.map((banner) => (banner.id === id ? newBanner : banner))
      );
    }
  };

  // 키오스크 섹션인지 확인
  const isKioskSection = mainTitle.includes("키오스크");

  // 키오스크가 아닌 경우에만 모션 적용
  const ContentWrapper = isKioskSection ? React.Fragment : motion.div;
  const motionProps = isKioskSection
    ? {}
    : {
        initial: "hidden",
        whileInView: "visible",
        viewport: { once: true, amount: 0.3 },
        variants: fadeInUp,
      };

  return (
    <ContentWrapper {...motionProps}>
      <ContentContainer direction={direction}>
        <TextContainer $textAlign={textAlign}>
          <MainTitle $textAlign={textAlign}>{mainTitle}</MainTitle>
          {subTitle && <SubTitle $textAlign={textAlign}>{subTitle}</SubTitle>}
          {mainTitle.includes("키오스크") && (
            <ButtonContainer>
              <KioskButton
                $isSelected={currentVideo === KIOSK_VIDEO_LIVE_USE}
                onClick={() =>
                  handleButtonClick(KIOSK_VIDEO_LIVE_USE, 800)
                }
              >
                실사용 영상
              </KioskButton>
              <KioskButton
                $isSelected={currentVideo === KIOSK_VIDEO_MEET_TEACHER}
                onClick={() =>
                  handleButtonClick(KIOSK_VIDEO_MEET_TEACHER, 1500)
                }
              >
                선생님 만나기
              </KioskButton>
              <KioskButton
                $isSelected={currentVideo === KIOSK_VIDEO_SELF_TREATMENT}
                onClick={() =>
                  handleButtonClick(KIOSK_VIDEO_SELF_TREATMENT, 1500)
                }
              >
                혼자서 치료하기
              </KioskButton>
            </ButtonContainer>
          )}
          {banners && banners.length > 0 && (
            <BannerGrid>
              {randomBanners.map((banner) => (
                <BannerImage
                  key={banner.id}
                  src={`${BANNER_IMAGE_BASE_URL}/${banner.fileName}.png`}
                  alt={`배너 ${banner.id}`}
                  onError={() => handleImageError(banner.id)}
                />
              ))}
            </BannerGrid>
          )}
          {children}
        </TextContainer>
        {media && media.length > 0 && (
          <MediaContainer>
            {media.map((item) =>
              item.type === "image" ? (
                <Image
                  key={item.src}
                  src={item.src}
                  alt={item.alt || "content-image"}
                  $maxWidth={item.maxWidth}
                />
              ) : (
                <Video
                  key={currentVideo}
                  autoPlay
                  muted
                  loop
                  $maxWidth={currentMaxWidth}
                  $videoType={
                    currentVideo?.includes("live_use") ? "live_use" : undefined
                  }
                >
                  <source src={currentVideo || item.src} type="video/mp4" />
                  Your browser does not support the video tag.
                </Video>
              )
            )}
          </MediaContainer>
        )}
      </ContentContainer>
    </ContentWrapper>
  );
};

export default ContentSection;
